import { useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { DEFAULT_PAGE_SIZE, URL_PARAMS } from '../constants';

export type UsePaginationReturn = {
  /** Current page size */
  pageSize: number;
  /** Cursor for forward pagination (current page's `after` cursor) */
  cursorAfter: string | undefined;
  /** Cursor for backward pagination */
  cursorBefore: string | undefined;
  /** Whether there are more results after current page */
  hasNextPage: boolean;
  /** Whether there are more results before current page (true when not on page 1) */
  hasPreviousPage: boolean;
  /** Set the page size */
  setPageSize: (size: number) => void;
  /** Set cursor for forward pagination */
  setCursorAfter: (cursor: string | undefined) => void;
  /** Set cursor for backward pagination */
  setCursorBefore: (cursor: string | undefined) => void;
  /** Set whether there is a next page */
  setHasNextPage: (hasNext: boolean) => void;
  /** Set whether there is a previous page */
  setHasPreviousPage: (hasPrev: boolean) => void;
  /**
   * Navigate to the next page by pushing the current page's end cursor onto
   * the stack and setting cursorAfter to endCursor.
   */
  goToNextPage: (endCursor: string) => void;
  /**
   * Navigate to the previous page by popping the cursor stack.
   * After popping, cursorAfter is set to the top of the remaining stack
   * (undefined when back on page 1).
   */
  goToPreviousPage: () => void;
  /**
   * Reset the cursor stack (call on search-term change so Previous is
   * disabled and the user starts over from page 1).
   */
  resetCursorStack: () => void;
};

/**
 * Hook for managing cursor-based pagination state with a true Previous page.
 *
 * Reads initial state from URL query parameters on mount:
 * - size: page size (defaults to DEFAULT_PAGE_SIZE)
 * - after: cursor for forward pagination
 * - before: cursor for backward pagination
 *
 * The cursor stack tracks cursors for pages already visited so that
 * goToPreviousPage returns to the actual previous page rather than jumping
 * back to page 1 (which setCursorAfter(undefined) would do).
 *
 * Next  → push currentCursorAfter onto the stack, set cursorAfter = endCursor
 * Prev  → pop the stack, set cursorAfter = popped value (undefined = page 1)
 * Reset → clear the stack and set cursorAfter = undefined (search change)
 *
 * @returns Pagination state and handlers
 */
export const usePagination = (): UsePaginationReturn => {
  const { search } = useLocation();

  // Parse initial state from URL on mount
  const initialState = useMemo(() => {
    const params = new URLSearchParams(search);
    const pageSizeFromUrl = params.get(URL_PARAMS.PAGE_SIZE);
    const initialCursorAfter = params.get(URL_PARAMS.CURSOR_AFTER) ?? undefined;
    const initialCursorBefore =
      params.get(URL_PARAMS.CURSOR_BEFORE) ?? undefined;

    const initialPageSize = pageSizeFromUrl
      ? parseInt(pageSizeFromUrl, 10)
      : DEFAULT_PAGE_SIZE;

    return {
      pageSize: initialPageSize,
      cursorAfter: initialCursorAfter,
      cursorBefore: initialCursorBefore,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reads URL search params once on mount to seed initial pagination state; subsequent URL changes are handled via explicit setters
  }, []); // Only run on mount

  // State management
  const [pageSize, setPageSize] = useState<number>(initialState.pageSize);
  const [cursorAfter, setCursorAfter] = useState<string | undefined>(
    initialState.cursorAfter
  );
  const [cursorBefore, setCursorBefore] = useState<string | undefined>(
    initialState.cursorBefore
  );
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  /**
   * Cursor stack — each entry is the `after` cursor that was active when
   * the user pressed Next.  The stack grows with Next and shrinks with Prev.
   * An empty stack means we are on page 1.
   *
   * hasPreviousPage is derived from the stack length rather than kept as a
   * separate state to avoid the stale-closure issues that arose when both
   * needed to be set in the same setState call.
   */
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  // Derived: Previous is available whenever the stack is non-empty.
  const hasPreviousPage = cursorStack.length > 0;

  // Memoized handlers to prevent unnecessary re-renders
  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
  }, []);

  const handleSetCursorAfter = useCallback((cursor: string | undefined) => {
    setCursorAfter(cursor);
  }, []);

  const handleSetCursorBefore = useCallback((cursor: string | undefined) => {
    setCursorBefore(cursor);
  }, []);

  const handleSetHasNextPage = useCallback((hasNext: boolean) => {
    setHasNextPage(hasNext);
  }, []);

  // hasPreviousPage is derived from cursorStack; this setter is kept for
  // API compatibility but is a no-op.
  const handleSetHasPreviousPage = useCallback(
    (_hasPrev: boolean) => undefined,
    []
  );

  const goToNextPage = useCallback(
    (endCursor: string) => {
      // Push the current cursorAfter (before moving forward) onto the stack
      setCursorStack((prev) => [...prev, cursorAfter]);
      setCursorAfter(endCursor);
    },
    [cursorAfter]
  );

  const goToPreviousPage = useCallback(() => {
    setCursorStack((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const restored = next.pop();
      setCursorAfter(restored);
      return next;
    });
  }, []);

  const resetCursorStack = useCallback(() => {
    setCursorStack([]);
    setCursorAfter(undefined);
  }, []);

  return useMemo(
    () => ({
      pageSize,
      cursorAfter,
      cursorBefore,
      hasNextPage,
      hasPreviousPage,
      setPageSize: handleSetPageSize,
      setCursorAfter: handleSetCursorAfter,
      setCursorBefore: handleSetCursorBefore,
      setHasNextPage: handleSetHasNextPage,
      setHasPreviousPage: handleSetHasPreviousPage,
      goToNextPage,
      goToPreviousPage,
      resetCursorStack,
    }),
    [
      pageSize,
      cursorAfter,
      cursorBefore,
      hasNextPage,
      hasPreviousPage,
      handleSetPageSize,
      handleSetCursorAfter,
      handleSetCursorBefore,
      handleSetHasNextPage,
      handleSetHasPreviousPage,
      goToNextPage,
      goToPreviousPage,
      resetCursorStack,
    ]
  );
};
